import { useEffect, useMemo, useRef, useState } from 'react';

import { playerSidebar } from '@/config/sidebarConfig.ts';
import { updateMeApi, uploadUserAvatarApi } from '@/lib/api/usersApi';
import { resendVerificationEmailApi } from '@/lib/auth/authApi';
import { getFileUrl } from '@/lib/file-url';
import {
  isoDateFieldOptional,
  licenseFieldOptional,
  personNameField,
  phoneFieldOptional,
  usernameFieldOptional,
} from '@/lib/validation/fields';
import { useAuthStore } from '@/stores/auth-store';
import { getInitials } from '@shared';
import { Gender } from '@shared/enums/domain.enum';
import { useMutation } from '@tanstack/react-query';
import { Calendar, Camera, Check, Mail, Phone, RefreshCw, Save, Shield, User } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import LicenseStatusBadge from '../../components/user/LicenseStatusBadge';

// ─── Schema ───────────────────────────────────────────────────

const ProfileSchema = z.object({
  firstName: personNameField('Le prénom'),
  lastName: personNameField('Le nom'),
  username: usernameFieldOptional,
  mobile: phoneFieldOptional,
  licenseNumber: licenseFieldOptional,
  birthDate: isoDateFieldOptional,
  gender: z.string().optional().or(z.literal('')),
});

type ProfileFormData = z.infer<typeof ProfileSchema>;
type ProfileFormErrors = Partial<Record<keyof ProfileFormData, string>>;

export default function PlayerProfile() {
  const user = useAuthStore(s => s.user);
  const updateUser = useAuthStore(s => s.updateUser);

  // ─── Avatar ───────────────────────────────────────────────
  const avatarFile = user?.files?.[0] ?? null;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(avatarFile ? getFileUrl(avatarFile) : null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  const [form, setForm] = useState<ProfileFormData>(() => ({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    username: user?.username ?? '',
    mobile: user?.mobile ?? '',
    licenseNumber: user?.licenseNumber ?? '',
    birthDate: user?.birthDate ? user.birthDate.slice(0, 10) : '',
    gender: user?.gender ?? '',
  }));
  const [errors, setErrors] = useState<ProfileFormErrors>({});
  const [resendCooldown, setResendCooldown] = useState(0);

  const resendMutation = useMutation({
    mutationFn: () => resendVerificationEmailApi(user!.email),
    onSuccess: () => {
      toast.success('Email de vérification envoyé');
      setResendCooldown(60);
    },
    onError: () => {
      toast.error("Erreur lors de l'envoi. Réessayez plus tard.");
    },
  });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const mutation = useMutation({
    mutationFn: async () => {
      // Upload avatar si un nouveau fichier est sélectionné
      if (selectedFile && user) {
        await uploadUserAvatarApi(user.id, selectedFile);
        setSelectedFile(null);
      }

      return updateMeApi({
        firstName: form.firstName,
        lastName: form.lastName || undefined,
        username: form.username || undefined,
        mobile: form.mobile || undefined,
        licenseNumber: form.licenseNumber || undefined,
        birthDate: form.birthDate || undefined,
        gender: form.gender || undefined,
      });
    },
    onSuccess: updated => {
      updateUser(updated);
      // Mettre à jour le preview avec le fichier retourné
      if (updated.files?.[0]) {
        setPreviewUrl(getFileUrl(updated.files[0]));
      }
      toast.success('Profil mis à jour avec succès');
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour du profil');
    },
  });

  const initialForm = useMemo<ProfileFormData>(
    () => ({
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      username: user?.username ?? '',
      mobile: user?.mobile ?? '',
      licenseNumber: user?.licenseNumber ?? '',
      birthDate: user?.birthDate ? user.birthDate.slice(0, 10) : '',
      gender: user?.gender ?? '',
    }),
    [user],
  );

  const isDirty = useMemo(
    () =>
      (Object.keys(initialForm) as (keyof ProfileFormData)[]).some(k => form[k] !== initialForm[k]) ||
      selectedFile !== null,
    [form, initialForm, selectedFile],
  );

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  function handleChange(key: keyof ProfileFormData, value: string) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function handleBlur(key: keyof ProfileFormData) {
    const fieldSchema = ProfileSchema.shape[key];
    const result = (fieldSchema as z.ZodTypeAny).safeParse(form[key]);
    setErrors(e => ({
      ...e,
      [key]: result.success ? undefined : result.error.issues[0]?.message,
    }));
  }

  function handleSubmit() {
    const result = ProfileSchema.safeParse(form);
    if (!result.success) {
      const errs = result.error.flatten().fieldErrors;
      setErrors(Object.fromEntries(Object.entries(errs).map(([k, v]) => [k, v?.[0]])));
      toast.error('Veuillez corriger les erreurs avant de sauvegarder');
      return;
    }
    mutation.mutate();
  }

  const fieldClass = (key: keyof ProfileFormData) =>
    `w-full px-4 py-2.5 border rounded-lg focus:outline-none transition ${
      errors[key] ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-orange-500'
    }`;

  if (!user) return null;

  const initials = getInitials(`${user.firstName ?? ''} ${user.lastName ?? ''}`);

  return (
    <DashboardLayout sidebarConfig={playerSidebar}>
      {/* Responsive container */}
      <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
        <PageHeader
          title='Mon Profil'
          subtitle='Modifiez vos informations personnelles'
          actions={
            <button
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className='w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-xl font-semibold transition shadow-lg shadow-orange-200'
            >
              <Save className='w-5 h-5' />
              {mutation.isPending ? 'Enregistrement...' : 'Sauvegarder'}
            </button>
          }
        />

        <div className='space-y-6'>
          {/* Avatar */}
          <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
            <h2 className='text-lg font-bold text-slate-900 mb-6'>Photo de profil</h2>

            <div className='flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6'>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={`${user.firstName} ${user.lastName}`}
                  className='w-24 h-24 rounded-2xl object-cover shadow-lg shrink-0'
                />
              ) : (
                <div className='w-24 h-24 bg-linear-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center text-white font-extrabold text-3xl shadow-lg shrink-0'>
                  {initials}
                </div>
              )}

              <div className='min-w-0'>
                <button
                  type='button'
                  onClick={() => fileInputRef.current?.click()}
                  className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:border-orange-300 transition text-sm font-semibold text-slate-700 cursor-pointer'
                >
                  <Camera className='w-4 h-4' />
                  {previewUrl ? 'Changer la photo' : 'Ajouter une photo'}
                </button>

                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/jpeg,image/png,image/gif,image/webp'
                  onChange={handleFileChange}
                  className='hidden'
                />

                <p className='text-xs text-slate-500 mt-2'>JPG, PNG ou GIF. Max 10 Mo.</p>
                <p className='text-xs text-slate-500'>Recommande : 300x300 px (carre).</p>

                <p className='font-semibold text-slate-900 mt-3 truncate'>
                  {user.firstName} {user.lastName}
                </p>
                <p className='text-sm text-slate-500 truncate'>Joueur{user.club ? ` - ${user.club.name}` : ''}</p>
              </div>
            </div>
          </div>

          {/* Informations personnelles */}
          <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
            <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
              <User className='w-5 h-5 text-orange-600' />
              Informations personnelles
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>Nom</label>
                <input
                  type='text'
                  value={form.lastName}
                  onChange={e => handleChange('lastName', e.target.value)}
                  onBlur={() => handleBlur('lastName')}
                  className={fieldClass('lastName')}
                />
                {errors.lastName && <p className='mt-1 text-xs text-red-500'>{errors.lastName}</p>}
              </div>

              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>Prénom</label>
                <input
                  type='text'
                  value={form.firstName}
                  onChange={e => handleChange('firstName', e.target.value)}
                  onBlur={() => handleBlur('firstName')}
                  className={fieldClass('firstName')}
                />
                {errors.firstName && <p className='mt-1 text-xs text-red-500'>{errors.firstName}</p>}
              </div>

              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>Nom d'utilisateur</label>
                <input
                  type='text'
                  value={form.username}
                  onChange={e => handleChange('username', e.target.value)}
                  onBlur={() => handleBlur('username')}
                  placeholder='@pseudo'
                  className={fieldClass('username')}
                />
                {errors.username && <p className='mt-1 text-xs text-red-500'>{errors.username}</p>}
              </div>

              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  <span className='flex items-center gap-1.5'>
                    <Calendar className='w-3.5 h-3.5' /> Date de naissance
                  </span>
                </label>
                <input
                  type='date'
                  value={form.birthDate}
                  onChange={e => handleChange('birthDate', e.target.value)}
                  className={fieldClass('birthDate')}
                />
                <p className='mt-1 text-xs text-slate-400'>Utilisée pour les catégories d'âge FFPJP</p>
              </div>

              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>Sexe</label>
                <select
                  value={form.gender}
                  onChange={e => handleChange('gender', e.target.value)}
                  className={fieldClass('gender')}
                >
                  <option value=''>Non renseigné</option>
                  <option value={Gender.MALE}>Homme</option>
                  <option value={Gender.FEMALE}>Femme</option>
                </select>
                <p className='mt-1 text-xs text-slate-400'>Utilisé pour les statistiques fédérales</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
            <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
              <Mail className='w-5 h-5 text-blue-600' />
              Contact
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  <span className='flex items-center gap-1.5'>
                    <Mail className='w-3.5 h-3.5' /> Email
                  </span>
                </label>

                <input
                  type='email'
                  value={user.email}
                  readOnly
                  className='w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed'
                />

                {user.emailVerified ? (
                  <div className='mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700'>
                    <Check className='w-3.5 h-3.5' />
                    Email vérifié
                  </div>
                ) : (
                  <div className='mt-2 space-y-1'>
                    <p className='text-xs text-amber-600 font-medium'>Email non vérifié</p>
                    <button
                      type='button'
                      disabled={resendMutation.isPending || resendCooldown > 0}
                      onClick={() => resendMutation.mutate()}
                      className='inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:text-slate-400 disabled:cursor-not-allowed transition'
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${resendMutation.isPending ? 'animate-spin' : ''}`} />
                      {resendCooldown > 0
                        ? `Renvoyer dans ${resendCooldown}s`
                        : resendMutation.isPending
                          ? 'Envoi...'
                          : "Renvoyer l'email de vérification"}
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  <span className='flex items-center gap-1.5'>
                    <Phone className='w-3.5 h-3.5' /> Téléphone mobile
                  </span>
                </label>
                <input
                  type='tel'
                  value={form.mobile}
                  onChange={e => handleChange('mobile', e.target.value)}
                  onBlur={() => handleBlur('mobile')}
                  placeholder='06 12 34 56 78'
                  className={fieldClass('mobile')}
                />
                {errors.mobile && <p className='mt-1 text-xs text-red-500'>{errors.mobile}</p>}
              </div>
            </div>
          </div>

          {/* Licence FFPJP */}
          <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
            <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
              <Shield className='w-5 h-5 text-purple-600' />
              Licence FFPJP
            </h2>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>Numéro de licence</label>
                <input
                  type='text'
                  value={form.licenseNumber}
                  onChange={e => handleChange('licenseNumber', e.target.value)}
                  onBlur={() => handleBlur('licenseNumber')}
                  placeholder='Ex : 069 12345'
                  className={`${fieldClass('licenseNumber')} font-mono`}
                />
                {errors.licenseNumber && <p className='mt-1 text-xs text-red-500'>{errors.licenseNumber}</p>}
                <div className='mt-2'>
                  <LicenseStatusBadge licenseNumber={form.licenseNumber || null} />
                </div>
              </div>

              {user.club && (
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-2'>Club</label>
                  <input
                    type='text'
                    value={user.club.name}
                    readOnly
                    className='w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed'
                  />
                  <p className='mt-1 text-xs text-slate-400'>Le club est géré par votre administrateur</p>
                </div>
              )}
            </div>
          </div>

          <div className='flex flex-row justify-end'>
            <button
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className='w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white rounded-xl font-semibold transition shadow-lg shadow-orange-200'
            >
              <Save className='w-5 h-5' />
              {mutation.isPending ? 'Enregistrement...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
